import SearchIcon from '@mui/icons-material/Search';
import { CardHeader, Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import { sanitizeCpfOrName } from 'utils/sanitizeCpfOrName';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import {
  resetSellerListPagination,
  restoreSellerStoreList,
  setAllSellerList,
  setSellerActive,
  setSellersFilters,
} from 'v3/data/store/seller/actions';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import { listAllSellers } from 'v3/services/api';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { MiniButton } from 'v3/shared/components/Button/MiniButton';
import { ActiveSellersEnum, ActiveStatusMap } from 'v3/shared/components/Filters/SellersFilter/enum';
import { AlignMiniButton, CardContentStyled, CardStyled } from 'v3/shared/components/Filters/SellersFilter/styles';
import { SellersFilterType } from 'v3/shared/components/Filters/SellersFilter/types';
import { DropdownSelectFormController } from 'v3/shared/components/Inputs/DropdownSelectFormController';
import { InputFormController } from 'v3/shared/components/Inputs/InputFormController';
import { MultiSelectFormController } from 'v3/shared/components/Inputs/MultiSelectFormController';
import { BodyLarge } from 'v3/shared/typography/BodyLarge';

export const SellersFilters = () => {
  const { control, reset, getValues, setValue } = useForm<SellersFilterType>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { status: ActiveSellersEnum.Todos },
  });

  const { storeList } = useStoreSelector((store) => store.seller);
  const { sellersFilter } = useStoreSelector((store) => store.seller);
  const { isTablet } = useBreakpoints();
  const { page, rowsPerPage } = sellersFilter;

  const handleClear = () => {
    reset();
    setSellersFilters('');
    setSellerActive(null);
    setValue('filter', undefined);
    setValue('status', ActiveSellersEnum.Todos);
    resetSellerListPagination();
    restoreSellerStoreList(storeList);
    fetchSellerList();
  };

  const fetchSellerList = async () => {
    const rawFilterValue = getValues('filter')?.trim() || '';
    const currentFilterValue = sanitizeCpfOrName(rawFilterValue);
    const currentStatusValue = getValues('status');
    resetSellerListPagination();

    try {
      const response = await listAllSellers({
        rowsPerPage,
        page,
        searchString: currentFilterValue,
        active: ActiveStatusMap(currentStatusValue),
        storeIds: sellersFilter.storeIds,
      });
      setAllSellerList(response.data);
      setSellersFilters(currentFilterValue);
      setSellerActive(ActiveStatusMap(currentStatusValue));
    } catch (error) {
      showErrorDialog('Erro ao obter lista dos consultores', error);
    }
  };

  return (
    <CardStyled>
      <CardHeader title={<BodyLarge>Filtro</BodyLarge>} />
      <CardContentStyled>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6.5, md: 6.5, lg: 2.5 }} component="div">
            <InputFormController
              data-testid="input-cpf-or-name"
              inputType="text"
              formControl={control}
              formControlName="filter"
              label="Buscar por CPF ou nome"
              placeholder="Digite o CPF ou nome do vendedor"
              fullWidth
              startAdornmentIcon={SearchIcon}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3 }} component="div">
            <DropdownSelectFormController
              data-testid="select-form-controller-status"
              formControl={control}
              formControlName="status"
              shouldRenderVoid={false}
              label="Status"
              enumType={true}
              selectValues={ActiveSellersEnum}
              onChangeSelect={(active: ActiveSellersEnum) => {
                setValue('status', active);
              }}
              key={getValues('status')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3, md: 3, lg: 3 }} component="div">
            <MultiSelectFormController
              data-testid="input-store"
              formControl={control}
              formControlName="store"
              label="Lista de lojas"
              setValue={setValue}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2, md: 2, lg: 2 }} component="div">
            <AlignMiniButton>
              <MiniButton text="Limpar" $backgroundColor="transparent" handleClick={handleClear} />

              {isTablet ? (
                <MiniButton icon={SearchIcon} handleClick={() => fetchSellerList()} />
              ) : (
                <FlexButton fullWidth text="Pesquisar" size="small" handleClick={() => fetchSellerList()} />
              )}
            </AlignMiniButton>
          </Grid>
        </Grid>
      </CardContentStyled>
    </CardStyled>
  );
};
